import {
  EditOutlined,
  FormOutlined,
  ProfileOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { AInput, AModal, ASwitch } from '../../../components';
import { IProductCategory } from '../../../shared/interfaces';
import { useToast } from '../../../hooks/toast';
import { useCallback, useEffect, useState } from 'react';
import { schemaProductCategory } from '../schemas/yup.product-category.schema';
import { Card, Form, Space, Typography } from 'antd';
import { patchProductCategory, postProductCategory } from '../../../http';
import { useI18n } from '../../../shared/i18n';

const { Text } = Typography;

interface IProductCategoryProps {
  open: boolean;
  close: () => void;
  category: IProductCategory | null;
  setCategories: React.Dispatch<React.SetStateAction<IProductCategory[]>>;
}

const ProductCategoryCreateEdit: React.FC<IProductCategoryProps> = ({
  open,
  close,
  category,
  setCategories,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const isEditing = !!category;
  const [description, setDescription] = useState<string>('');
  const [status, setStatus] = useState<boolean>(false);

  const resetForm = useCallback(() => {
    setDescription('');
    setStatus(false);
  }, []);

  const handleCreate = useCallback(async () => {
    schemaProductCategory
      .validate({
        description,
      })
      .then(async (validated) => {
        await postProductCategory(validated)
          .then((data) => {
            setCategories((state) => [...state, data]);
            addToast({
              type: 'success',
              title: t('productCategoriesModal.toast.successTitle'),
              description: t('productCategoriesModal.toast.createdDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('productCategoriesModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('productCategoriesModal.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('productCategoriesModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [description, setCategories, addToast, close, t]);

  const handleEdit = useCallback(async () => {
    schemaProductCategory
      .validate({
        description,
        status,
      })
      .then(async (validated) => {
        await patchProductCategory(category?.id, validated)
          .then((data) => {
            setCategories((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: t('productCategoriesModal.toast.infoTitle'),
              description: t('productCategoriesModal.toast.updatedDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('productCategoriesModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('productCategoriesModal.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('productCategoriesModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [description, status, category?.id, setCategories, addToast, close, t]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (category) {
      setDescription(category.description);
      setStatus(!!category.status);
      return;
    }

    resetForm();
  }, [open, category, resetForm]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        isEditing ? (
          <>
            <EditOutlined /> {t('productCategoriesModal.titleEdit')}
          </>
        ) : (
          <>
            <FormOutlined /> {t('productCategoriesModal.titleCreate')}
          </>
        )
      }
      onOk={isEditing ? handleEdit : handleCreate}
      width={620}
    >
      <Form layout="vertical" style={{ maxWidth: '100%', marginTop: 8 }}>
        <Card size="small" style={{ marginBottom: isEditing ? 12 : 0 }}>
          <Form.Item
            label={
              <Space size={6}>
                <TagsOutlined style={{ color: '#2f54eb' }} />
                <span>{t('productCategoriesModal.fields.description')}</span>
              </Space>
            }
            required
          >
            <AInput
              placeholder={t(
                'productCategoriesModal.fields.descriptionPlaceholder',
              )}
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('productCategoriesModal.fields.descriptionHelp')}
          </Text>
        </Card>

        {isEditing && (
          <Card size="small">
            <Form.Item
              label={t('productCategoriesModal.fields.status')}
              style={{ marginBottom: 0 }}
            >
              <Space align="center" size={10}>
                <ASwitch
                  checked={status}
                  checkedChildren={t('common.yes')}
                  unCheckedChildren={t('common.no')}
                  onChange={() => {
                    setStatus(!status);
                  }}
                />
                <Space size={6}>
                  <ProfileOutlined style={{ color: '#13c2c2' }} />
                  <Text type="secondary">
                    {status
                      ? t('productCategoriesModal.fields.activeDescription')
                      : t('productCategoriesModal.fields.inactiveDescription')}
                  </Text>
                </Space>
              </Space>
            </Form.Item>
          </Card>
        )}
      </Form>
    </AModal>
  );
};

export default ProductCategoryCreateEdit;
